import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ToggleSwitch = ({ enabled, onChange }) => (
  <motion.button
    type="button"
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
    onClick={onChange}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <motion.span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`}
      animate={{ x: enabled ? 16 : 2 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
    />
  </motion.button>
);

const SystemNotifications = () => {
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [calendarEnabled, setCalendarEnabled] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <motion.div
      className="mx-auto w-[92%] max-w-2xl rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:w-full md:p-5"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="mb-3 flex items-center" variants={itemVariants}>
        <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
          <svg
            className="h-5 w-5 text-blue-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
        </div>
        <div className="flex flex-1 items-center justify-between">
          <h2 className="text-xl font-semibold text-black">System Notifications</h2>
          <motion.a
            href="#"
            className="ml-3 text-xs font-medium text-blue-600 hover:text-blue-700"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            More
          </motion.a>
        </div>
      </motion.div>

      <motion.p className="mb-4 text-sm text-black" variants={itemVariants}>
        Stay informed with all important updates and announcements.
      </motion.p>

      <div className="space-y-2">
        <motion.div
          className="flex items-center justify-between rounded-lg bg-gray-50 p-3"
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
          transition={{ type: 'spring', stiffness: 400 }}
        >
          <div className="flex items-center">
            <svg
              className="mr-3 h-4 w-4 text-blue-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l9 6 9-6" />
            </svg>
            <div>
              <div className="text-sm font-medium text-black">Email Notifications</div>
              <div className="text-xs text-gray-500">Receive updates via email</div>
            </div>
          </div>
          <ToggleSwitch enabled={emailEnabled} onChange={() => setEmailEnabled(!emailEnabled)} />
        </motion.div>

        <motion.div
          className="flex items-center justify-between rounded-lg bg-gray-50 p-3"
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
          transition={{ type: 'spring', stiffness: 400 }}
        >
          <div className="flex items-center">
            <svg
              className="mr-3 h-4 w-4 text-blue-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 15a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v8z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7 10h.01M12 10h.01M17 10h.01"
              />
            </svg>
            <div>
              <div className="text-sm font-medium text-black">Push Notifications</div>
              <div className="text-xs text-gray-500">Get instant notifications</div>
            </div>
          </div>
          <ToggleSwitch enabled={pushEnabled} onChange={() => setPushEnabled(!pushEnabled)} />
        </motion.div>

        <motion.div
          className="flex items-center justify-between rounded-lg bg-gray-50 p-3"
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
          transition={{ type: 'spring', stiffness: 400 }}
        >
          <div className="flex items-center">
            <svg
              className="mr-3 h-4 w-4 text-blue-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            <div>
              <div className="text-sm font-medium text-black">Calendar Updates</div>
              <div className="text-xs text-gray-500">Sync with calendar</div>
            </div>
          </div>
          <ToggleSwitch
            enabled={calendarEnabled}
            onChange={() => setCalendarEnabled(!calendarEnabled)}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default SystemNotifications;
